import { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import { colors, fonts } from '../../styles/theme';

const CheckboxLabelWrapper = styled('div')`
  color: ${colors.grey.altDark};
  display: block;
  height: 18px;
  margin-right: 7px;
  position: relative;
  width: 18px;
  & + label {
    font-family: ${fonts.sans.family};
    font-size: 14px;
    font-weight: normal;
    letter-spacing: 0;
    line-height: 18px;
    text-transform: none;
    margin-bottom: 0;
    &:hover {
      cursor: pointer;
    }
  }
`;

/**
 * Checkbox input that isn't visible but when focus should
 * highlight fake visual
 */
export const OriginalCheckboxInput = styled('input')`
  margin-right: 0;
  margin-top: 2px;
  margin-left: 2px;
  &:focus + label {
    border-color: ${colors.blue.primary};
  }
`;

const customCheckbox = css`
  background: ${colors.white};
  border: 1px solid ${colors.grey.secondaryDark};
  cursor: pointer;
  height: 18px;
  left: 0;
  position: absolute;
  top: 0;
  width: 18px;
  border-radius: 3px;
  margin: 0;
`;

const customCheckmark = css`
  background: transparent;
  content: ' ';
  height: 4px;
  left: 3px;
  position: absolute;
  top: 4px;
  transform: rotate(-45deg);
  width: 8px;
  border: 2px solid ${colors.black};
  border-top: none;
  border-right: none;
`;

const uncheckedStyles = css`
  ${customCheckbox};
  &::after {
    ${customCheckmark};
    opacity: 0;
  }
  &:hover::after {
    opacity: 0.5;
  }
`;

const checkedStyles = css`
  ${customCheckbox};
  &::after {
    ${customCheckmark};
    opacity: 1;
  }
`;

const CustomCheckbox = styled('label')`
  ${(props) => (props.checked ? `${checkedStyles}` : `${uncheckedStyles}`)};
`;

const disabledStyles = css`
  ${CustomCheckbox} {
    border-color: ${colors.grey.secondary};
    background-color: ${colors.grey.secondary};
    &:after {
      opacity: 0;
    }
    &:hover {
      cursor: not-allowed;
    }
  }
  ${CheckboxLabelWrapper} + label {
    color: ${colors.grey.secondaryDark};
    &:hover {
      cursor: not-allowed;
    }
  }
`;

const CheckBoxContainer = styled('div')`
  color: ${colors.black};
  display: flex;
  margin-bottom: 6px;
  &:only-child,
  &:last-child {
    margin-bottom: 0;
  }
  ${(props) => (props.disabled ? `${disabledStyles}` : ``)};
`;

/**
 * This Component renders a checkbox and a label to the side.
 *
 * It sends an onChange event when its checked value changes.
 * @props {boolean} checked
 */
class Checkbox extends Component {
  constructor(props) {
    super(props);
    this.state = {
      checked: props.checked || false,
      disabled: props.disabled || false,
    };
  }

   
  UNSAFE_componentWillReceiveProps(props) {
    this.setState({ checked: props.checked, disabled: props.disabled });
  }

  toggle = () => {
    const { checked, disabled } = this.state;
    if (disabled) return;
    const { onChange } = this.props;
    const newCheckedState = !checked;
    this.setState({ checked: newCheckedState });
    if (onChange) onChange(newCheckedState);
  };

  render() {
    const { label, value } = this.props;
    const { checked, disabled } = this.state;
    return (
      <CheckBoxContainer disabled={disabled}>
        <CheckboxLabelWrapper>
          <OriginalCheckboxInput
            type="checkbox"
            checked={checked}
            onChange={this.toggle}
            value={value}
            disabled={disabled}
          />
          { }
          <CustomCheckbox checked={checked} onClick={this.toggle} />
        </CheckboxLabelWrapper>
        {typeof label === 'string' ? (
           
          <label onClick={this.toggle}>{label}</label>
        ) : (
           
          <label onClick={this.toggle}>{label}</label>
        )}
      </CheckBoxContainer>
    );
  }
}

Checkbox.defaultProps = {
  label: '',
  checked: false,
  value: '',
  disabled: false,
  onChange: () => undefined,
};
Checkbox.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  onChange: PropTypes.func,
};

export default Checkbox;
